import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Package, Truck, ArrowLeft, CreditCard, Store } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Checkout() {
  const { user, loading } = useAuth();
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [payment, setPayment] = useState("online");
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "TX", zip: "", phone: "" });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!loading && !user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="bg-sand/90 border border-tan/40 p-8 float-card">
          <h1 className="font-serif text-2xl text-navy">Sign in to order</h1>
          <p className="text-sm text-charcoal/80 mt-3">Texas law requires age verification before purchase. Create a customer account or sign in.</p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/signup" className="bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Create Account</Link>
            <Link to="/login" className="border border-navy text-navy px-6 py-3 text-xs uppercase tracking-[0.2em]">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  if (user && user.role !== "customer") {
    return <div className="max-w-md mx-auto px-6 py-20 text-center text-white"><p>Only customer accounts can place orders.</p></div>;
  }

  if (items.length === 0) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="bg-sand/90 border border-tan/40 p-8 float-card">
          <h1 className="font-serif text-2xl text-navy">Your cart is empty</h1>
          <Link to="/shop" className="mt-6 inline-block bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Browse the catalog</Link>
        </div>
      </div>
    );
  }

  const submit = async (ev) => {
    ev.preventDefault();
    setError(""); setBusy(true);
    try {
      const order = await api.post("/orders", {
        items: items.map((it) => ({ product_id: it.product_id, name: it.name, price_display: it.price_display, qty: it.qty, variant_id: it.variant_id || "", variant_label: it.variant_label || "" })),
        fulfillment,
        shipping_address: fulfillment === "shipping" ? addr : null,
        notes,
        payment_method: payment,
      });

      if (payment === "online") {
        const res = await api.post("/checkout/session", {
          order_id: order.data.id,
          origin_url: window.location.origin,
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        throw new Error("Could not start payment.");
      }

      clear();
      toast.success("Order placed! Pay at pickup or on delivery.");
      navigate("/account");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not place order.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
      <Link to="/cart" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white"><ArrowLeft size={14} /> Back to cart</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-sand/90 backdrop-blur-sm border border-tan/50 p-8 lg:p-10 float-card space-y-7">
          <div>
            <span className="eyebrow">Checkout</span>
            <h1 className="font-serif text-3xl text-navy mt-2">Complete your order</h1>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-charcoal/80 mb-3">Fulfillment</div>
            <div className="grid grid-cols-2 gap-3">
              <label data-testid="fulfillment-pickup" className={`cursor-pointer border p-5 ${fulfillment === "pickup" ? "border-navy bg-white" : "border-tan/40 bg-white/60"}`}>
                <input type="radio" name="ff" value="pickup" checked={fulfillment === "pickup"} onChange={() => setFulfillment("pickup")} className="hidden" />
                <div className="flex items-center gap-2"><Package size={18} className="text-navy" /><span className="font-serif text-lg text-navy">Store Pickup</span></div>
                <p className="text-xs text-charcoal/70 mt-2">Pick up at our Texarkana TX location. We'll text you when ready.</p>
              </label>
              <label data-testid="fulfillment-shipping" className={`cursor-pointer border p-5 ${fulfillment === "shipping" ? "border-navy bg-white" : "border-tan/40 bg-white/60"}`}>
                <input type="radio" name="ff" value="shipping" checked={fulfillment === "shipping"} onChange={() => setFulfillment("shipping")} className="hidden" />
                <div className="flex items-center gap-2"><Truck size={18} className="text-navy" /><span className="font-serif text-lg text-navy">Ship to Door</span></div>
                <p className="text-xs text-charcoal/70 mt-2">Discreet packaging. Tracking number provided once shipped.</p>
              </label>
            </div>
          </div>

          {fulfillment === "shipping" && (
            <div className="space-y-3 border-t border-tan/40 pt-5">
              <div className="text-xs uppercase tracking-wide text-charcoal/80">Shipping address</div>
              <input data-testid="addr-line1" required value={addr.line1} placeholder="Street address" onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className="w-full border border-tan/50 px-4 py-3 bg-white" />
              <input value={addr.line2} placeholder="Apt / Suite (optional)" onChange={(e) => setAddr({ ...addr, line2: e.target.value })} className="w-full border border-tan/50 px-4 py-3 bg-white" />
              <div className="grid grid-cols-3 gap-3">
                <input data-testid="addr-city" required value={addr.city} placeholder="City" onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="border border-tan/50 px-4 py-3 bg-white" />
                <input data-testid="addr-state" required value={addr.state} placeholder="State" maxLength={2} onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase() })} className="border border-tan/50 px-4 py-3 bg-white" />
                <input data-testid="addr-zip" required value={addr.zip} placeholder="ZIP" onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className="border border-tan/50 px-4 py-3 bg-white" />
              </div>
              <input value={addr.phone} placeholder="Phone (for delivery)" onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className="w-full border border-tan/50 px-4 py-3 bg-white" />
            </div>
          )}

          <div className="border-t border-tan/40 pt-5">
            <div className="text-xs uppercase tracking-wide text-charcoal/80 mb-3">Payment</div>
            <div className="grid grid-cols-2 gap-3">
              <label data-testid="payment-online" className={`cursor-pointer border p-5 ${payment === "online" ? "border-navy bg-white" : "border-tan/40 bg-white/60"}`}>
                <input type="radio" name="pm" value="online" checked={payment === "online"} onChange={() => setPayment("online")} className="hidden" />
                <div className="flex items-center gap-2"><CreditCard size={18} className="text-navy" /><span className="font-serif text-lg text-navy">Pay Online</span></div>
                <p className="text-xs text-charcoal/70 mt-2">Secure card payment via Stripe. Confirmed instantly.</p>
              </label>
              <label data-testid="payment-in-person" className={`cursor-pointer border p-5 ${payment === "in_person" ? "border-navy bg-white" : "border-tan/40 bg-white/60"}`}>
                <input type="radio" name="pm" value="in_person" checked={payment === "in_person"} onChange={() => setPayment("in_person")} className="hidden" />
                <div className="flex items-center gap-2"><Store size={18} className="text-navy" /><span className="font-serif text-lg text-navy">Pay at Pickup / Delivery</span></div>
                <p className="text-xs text-charcoal/70 mt-2">Reserve now, pay when you collect or receive your order.</p>
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide text-charcoal/80">Notes (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 w-full border border-tan/50 px-4 py-3 bg-white" />
          </div>

          {error && <div data-testid="checkout-error" className="text-sm text-texasred">{error}</div>}

          <button data-testid="checkout-submit" disabled={busy} className="w-full bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-charcoal disabled:opacity-50">
            {busy ? "Processing…" : payment === "online" ? `Pay ${money(subtotal)} Online` : "Place Order — Pay Later"}
          </button>
          <p className="text-[11px] text-charcoal/70">By placing this order you confirm you are 21+ and agree to all site terms.</p>
        </form>

        <aside className="bg-sand/90 backdrop-blur-sm border border-tan/50 p-7 float-card h-fit">
          <div className="eyebrow">Order Summary</div>
          <ul className="mt-4 space-y-3">
            {items.map((it) => (
              <li key={it.key} className="flex justify-between gap-3 text-sm">
                <span className="text-charcoal/90">{it.qty}× {it.name}{it.variant_label ? ` (${it.variant_label})` : ""}</span>
                <span className="text-navy font-medium whitespace-nowrap">{money(it.unit_price * it.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 border-t border-tan/40 pt-4 flex justify-between items-center">
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal/70">Total</span>
            <span data-testid="checkout-total" className="font-serif text-2xl text-navy">{money(subtotal)}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
